import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Tenta extrair texto de envelopes JSON ({"message":"..."} / {"resposta":"..."})
// e blocos ```json ... ```. Usado quando o agente devolveu JSON cru por engano
// e a mensagem já foi persistida assim.
export function unwrapAssistantJson(raw: string): string {
  if (!raw) return raw;
  const trimmed = raw.trim();
  const fence = trimmed.match(/^```(?:json|JSON)?\s*([\s\S]*?)\s*```$/);
  const candidate = fence ? fence[1].trim() : trimmed;
  if (!candidate.startsWith("{") || !candidate.endsWith("}")) return raw;
  try {
    const obj = JSON.parse(candidate);
    if (obj && typeof obj === "object") {
      for (const k of ["message", "resposta", "response", "text", "content", "reply", "answer"]) {
        const v = (obj as any)[k];
        if (typeof v === "string" && v.trim()) return v;
      }
    }
  } catch { /* não era JSON válido */ }
  return raw;
}

const BARE_URL_PATTERN =
  /\b((?:https?:\/\/|www\.)[^\s<]+[^\s<.,;:!?)}\]'"])/gi;

function linkifyBareUrls(html: string): string {
  return html
    .split(/(<a\b[^>]*>[\s\S]*?<\/a>)/gi)
    .map((part) => {
      if (/^<a\b/i.test(part)) return part;

      return part.replace(BARE_URL_PATTERN, (match) => {
        const trailingMatch = match.match(/([.,;:!?)]+)$/);
        const trailing = trailingMatch?.[1] ?? "";
        const url = trailing ? match.slice(0, -trailing.length) : match;
        const href = /^www\./i.test(url) ? `https://${url}` : url;
        return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="underline chat-link">${url}</a>${trailing}`;
      });
    })
    .join("");
}

export function formatWhatsAppMarkdown(text: string): string {
  if (!text) return text;
  // Desempacotar envelopes JSON antes de formatar
  text = unwrapAssistantJson(text);
  // Preservar blocos de code para não "poluir" conteúdo com substituições posteriores.
  // Mantemos a implementação intentionally simples e compatível com o comportamento atual.
  const codeBlocks: string[] = [];
  text = text.replace(/```([^`]+)```/g, (_match, code) => {
    const idx = codeBlocks.length;
    codeBlocks.push(String(code));
    return `__CODE_BLOCK_${idx}__`;
  });

  // Primeiro convertemos variações de negrito com duplo asterisco (**texto**),
  // para evitar match parcial do padrão *texto* e sobrar asterisco literal no UI.
  text = text.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-bold">$1</strong>');

  // Depois convertemos negrito com asterisco simples (*texto*)
  text = text.replace(/\*([^\*]+)\*/g, '<strong class="font-bold">$1</strong>');

  // Itálico com underline duplo (__texto__)
  text = text.replace(/__([^_]+)__/g, '<em class="italic">$1</em>');

  // Itálico com underscore simples (_texto_)
  text = text.replace(/_([^_]+)_/g, '<em class="italic">$1</em>');

  // Converter links Markdown [texto](url) → link clicável com a URL
  text = text.replace(/\[([^\]]+)\]\((https?:\/\/[^\)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="underline chat-link">$2</a>');

  // Processar riscado (~texto~)
  text = text.replace(/~([^~]+)~/g, '<span class="line-through">$1</span>');

  // URLs soltas (ex.: www.site.com ou https://site.com) → link clicável
  text = linkifyBareUrls(text);

  // Restaurar code blocks por último.
  text = text.replace(/__CODE_BLOCK_(\d+)__/g, (_match, idx) => {
    const i = Number(idx);
    const code = codeBlocks[i] ?? "";
    return `<code class="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">${code}</code>`;
  });

  return text;
}
